import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Card, CardBody, CardFooter, CardHeader, Col, Container, Row } from 'reactstrap';

import Filters from './Components/Filters';
import ReconTable from './Components/ReconTable';
import { Constant } from '../../../../../../../Helpers/constant';
import UploadFileModal from './Components/Modals/UploadFileModal';
import { BreadCrumb, Loader, TablePagination } from '../../../../../../../Common';
import ProgramPricingDetails from './Components/Modals/ProgramPricingDetailsModal';
import {
  GET_RECON_DETAILS,
  GET_RECON_DETAILS_EMPTY,
  GET_RECORD_DATA_EMPTY,
  GET_TRACKER_DATA,
  GET_TRACKER_DATA_EMPTY,
  GET_RECON_PREVIEW,
  GET_RECON_PREVIEW_EMPTY,
  PUBLISH_DATA_WITH_AUTH_EMPTY,
  PUBLISH_RECORD_DATA_EMPTY,
  SEND_APPROVAL_MAIL_EMPTY,
  UPDATE_RECORD_DATA_EMPTY,
  UPLOAD_CONFIG_EXCEL,
  UPLOAD_CONFIG_EXCEL_EMPTY,
} from '../../../../../../../../store/actions';

const BookingReconDetails = ({ onView, currentPageNumber, setCurrentPageNumber, filters, setFilters, headerToggler }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  /* ---------------------------- LOCAL STATES ---------------------------- */
  const [loading, setLoading] = useState(false);

  const [isShowUploadFile, setIsShowUploadFile] = useState(false);

  const [currentSelectedDetails, setCurrentSelectedDetails] = useState(null);
  const [isShowProgramPricingDetails, setIsShowProgramPricingDetails] = useState(false);

  const [currentPageNo, setCurrentPageNo] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({ id: `1`, name: `All records`, value: `` });

  const [isBenefitGroupSelected, setIsBenefitGroupSelected] = useState(false);

  /* ---------------------------- REDUX STATES ---------------------------- */
  //  RECON DETAILS
  const { reconDetails, reconDetailsLoading } = useSelector(state => ({
    reconDetails: state?.GetReconDetails?.data,
    reconDetailsLoading: state?.GetReconDetails?.loading,
  }));

  //  RECON PREVIEW
  const { reconPreviewData, reconPreviewLoading } = useSelector(state => ({
    reconPreviewData: state?.GetReconPreview?.data,
    reconPreviewLoading: state?.GetReconPreview?.loading,
  }));

  //  UPLOAD FILE
  const { uploadFile, uploadFileLoading } = useSelector(state => ({
    uploadFile: state?.UploadConfigExcel?.data,
    uploadFileLoading: state?.UploadConfigExcel?.loading,
  }));

  //  TRACKER DATA
  const { trackerList, trackerListLoading } = useSelector(state => ({
    trackerList: state?.GetTrackerData?.data,
    trackerListLoading: state?.GetTrackerData?.loading,
  }));

  /* ---------------------------- DISPATCHING ---------------------------- */
  useEffect(() => {
    dispatch({
      type: GET_RECON_DETAILS,
      payload: {
        urlParam: {
          skip: 0,
          limit: Constant.TABLE_PAGE_SIZE,
        },
      },
    });

    return () => {
      dispatch({
        type: SEND_APPROVAL_MAIL_EMPTY,
      });

      dispatch({
        type: GET_RECON_DETAILS_EMPTY,
      });

      dispatch({
        type: PUBLISH_RECORD_DATA_EMPTY,
      });

      dispatch({
        type: PUBLISH_DATA_WITH_AUTH_EMPTY,
      });

      dispatch({
        type: GET_RECORD_DATA_EMPTY,
      });

      dispatch({
        type: UPDATE_RECORD_DATA_EMPTY,
      });

      dispatch({
        type: GET_TRACKER_DATA_EMPTY,
      });

      dispatch({
        type: UPLOAD_CONFIG_EXCEL_EMPTY,
      });
    };
  }, []);

  useEffect(() => {
    if (reconPreviewData !== null) {
      setIsShowProgramPricingDetails(true);
    }
  }, [reconPreviewData]);

  useEffect(() => {
    if (uploadFile !== null) {
      let urlParams = {
        skip: currentPageNumber * Constant.TABLE_PAGE_SIZE,
        limit: Constant?.TABLE_PAGE_SIZE,
        fileTrackerId: uploadFile?.fileTrackerId,
        orderBy: `_created_at`,
        fileType: `BOOKING-RECON`,
      };

      dispatch({
        type: GET_TRACKER_DATA,
        payload: {
          urlParam: {
            ...urlParams,
          },
        },
      });

      dispatch({
        type: UPLOAD_CONFIG_EXCEL_EMPTY,
      });

      dispatch({
        type: GET_RECON_PREVIEW_EMPTY,
      });
    }
  }, [uploadFile]);

  useEffect(() => {
    if (trackerList !== null) {
      onView(trackerList?.results?.[0]);
      dispatch({
        type: GET_TRACKER_DATA_EMPTY,
      });
    }
  }, [trackerList]);

  /* ---------------------------- PAGINATION HANDLER ---------------------------- */
  const paginationHandler = pageNumber => {
    setCurrentPageNumber(pageNumber);

    let urlParams = {};
    dispatch({
      type: GET_RECON_DETAILS,
      payload: {
        urlParam: {
          ...urlParams,
          skip: pageNumber * Constant.TABLE_PAGE_SIZE,
          limit: Constant?.TABLE_PAGE_SIZE,
        },
      },
    });
  };

  /* ---------------------------- TABLE ACTION HANDLER ---------------------------- */
  const tableActionHandler = params => {
    setCurrentSelectedDetails(params?.details);

    if (params?.actionType === `RECON_DOWNLOAD`) {
      setCurrentPageNo(0);
      setIsBenefitGroupSelected(false);
      dispatch({
        type: GET_RECON_PREVIEW,
        payload: {
          urlParam: {
            skip: 0,
            limit: Constant.TABLE_PAGE_SIZE,
            status: appliedFilters?.value,
            ...(params?.details?.reconType?.code === 1 && { partnerId: params?.details?.partner?.id }),
            ...(params?.details?.reconType?.code === 2 && { projectId: params?.details?.program?.id }),
          },
          data: {
            month: `${params?.details?.month || ''}`,
            year: `${params?.details?.year || ''}`,
            reconType: params?.details?.reconType?.code,
            configId: `${params?.details?.configId || ''}`,
          },
        },
      });
    } else if (params?.actionType === `RECON_UPLOAD`) {
      setIsShowUploadFile(true);
      setIsBenefitGroupSelected(false);
    }
  };

  /* ---------------------------- PAGE CHANGE HANDLER ---------------------------- */
  const pageChangeHandler = pageNumber => {
    setCurrentPageNo(pageNumber);
    dispatch({
      type: GET_RECON_PREVIEW,
      payload: {
        urlParam: {
          skip: pageNumber * Constant.TABLE_PAGE_SIZE,
          limit: Constant.TABLE_PAGE_SIZE,
          status: appliedFilters?.value,
        },
        data: {
          month: `${currentSelectedDetails?.month || ''}`,
          year: `${currentSelectedDetails?.year || ''}`,
          reconType: currentSelectedDetails?.reconType?.code,
          configId: `${currentSelectedDetails?.configId || ''}`,
        },
      },
    });
  };

  /* ---------------------------- UPLOAD FILE HANDLER ---------------------------- */
  const onUploadFile = data => {
    dispatch({
      type: UPLOAD_CONFIG_EXCEL,
      payload: {
        data: data,
      },
    });
  };

  /* ---------------------------- ON FILTER HANDLER ---------------------------- */
  const filterOnProgramPricing = filter => {
    setAppliedFilters(filter);
    setCurrentPageNo(0);

    dispatch({
      type: GET_RECON_PREVIEW,
      payload: {
        urlParam: {
          skip: 0,
          limit: Constant.TABLE_PAGE_SIZE,
          status: filter?.value,
        },
        data: {
          month: `${currentSelectedDetails?.month || ''}`,
          year: `${currentSelectedDetails?.year || ''}`,
          reconType: currentSelectedDetails?.reconType?.code,
          configId: `${currentSelectedDetails?.configId || ''}`,
        },
      },
    });
  };

  /* ---------------------------- LOADER HANDLER ---------------------------- */
  useEffect(() => {
    if (reconDetailsLoading || reconPreviewLoading || trackerListLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [reconDetailsLoading, reconPreviewLoading, trackerListLoading]);

  /* ---------------------------- BREADCRUM HANDLER ---------------------------- */
  const breadcrumNavigationHandler = title => {
    if (title === `Dashboard`) {
      history.push('/dashboard');
    } else if (title === `Booking Recon`) {
      dispatch({
        type: GET_RECON_PREVIEW_EMPTY,
      });
    } else if (title === `Preview`) {
      //
    }
  };

  /* ---------------------------- ON CLOSE PROGRAM PREVIEW HANDLER ---------------------------- */
  const onCloseProgramPreview = () => {
    setIsShowProgramPricingDetails(false);
    setCurrentPageNo(0);
    setAppliedFilters({ id: `1`, name: `All records`, value: `` });

    dispatch({
      type: GET_RECON_PREVIEW_EMPTY,
    });
  };

  return (
    <Container fluid className="position-relative">
      {loading && <Loader />}

      {isShowProgramPricingDetails && (
        <ProgramPricingDetails
          pricingData={currentSelectedDetails}
          reconPreviewData={reconPreviewData}
          onClose={onCloseProgramPreview}
          onUpload={() => setIsShowUploadFile(true)}
          onFilter={filterOnProgramPricing}
          currentSelectedDetails={currentSelectedDetails}
          appliedFilters={appliedFilters}
          onChangePage={pageChangeHandler}
          currentPageNumber={currentPageNo}
        />
      )}
      {isShowUploadFile && (
        <UploadFileModal
          data={currentSelectedDetails}
          isLoading={uploadFileLoading}
          onUpload={onUploadFile}
          onClose={() => setIsShowUploadFile(false)}
        />
      )}

      <BreadCrumb title="Booking Recon" navigationby={breadcrumNavigationHandler} navigation={['Dashboard', 'Booking Recon']} />
      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader className="d-flex">
              <h4 className="card-title mb-0 flex-grow-1">Booking Recon</h4>

              <div className="btn-group custom-btn-group" role="group" aria-label="Basic radio toggle button group">
                <input
                  type="radio"
                  className="btn-check"
                  name="peakType"
                  id="btnradio1"
                  autoComplete="off"
                  value={1}
                  defaultChecked
                  onChange={() => headerToggler(`PROGRAM_PRICING`)}
                />
                <label className="btn btn-outline-primary" htmlFor="btnradio1">
                  Booking Recon
                </label>

                <input
                  type="radio"
                  className="btn-check"
                  name="peakType"
                  id="btnradio2"
                  autoComplete="off"
                  value={2}
                  onChange={() => headerToggler(`PRICING_UPLOADS`)}
                />
                <label className="btn btn-outline-primary" htmlFor="btnradio2">
                  Recon Uploads
                </label>
              </div>
            </CardHeader>

            <CardBody>
              <>
                <Filters
                  filters={filters}
                  setFilters={setFilters}
                  currentPageNumber={currentPageNumber}
                  setCurrentPageNumber={setCurrentPageNumber}
                />
                {reconDetails?.results?.length > 0 && (
                  <Col md={12}>
                    <h6 className="mb-3">{filters?.status?.label}</h6>
                  </Col>
                )}
                <ReconTable
                  data={reconDetails?.results ?? []}
                  totalData={reconDetails?.count}
                  pageHandler={paginationHandler}
                  pageNumber={currentPageNumber}
                  actionHandler={tableActionHandler}
                />
              </>
            </CardBody>

            {reconDetails?.count > 0 && (
              <CardFooter>
                <Row className="custom_pagination">
                  <Col md={12} className="d-flex justify-content-between align-items-center">
                    <div>
                      Showing <b>{currentPageNumber * Constant?.TABLE_PAGE_SIZE + 1}</b> to&nbsp;
                      <b>{currentPageNumber * Constant?.TABLE_PAGE_SIZE + reconDetails?.results?.length}</b> of <b>{reconDetails?.count}</b> records
                    </div>
                    <div className="d-flex justify-content-end">
                      <TablePagination totalData={reconDetails?.count} currentPage={currentPageNumber} onPageChangeHandler={paginationHandler} />
                    </div>
                  </Col>
                </Row>
              </CardFooter>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default BookingReconDetails;
